import React, { useState, useEffect } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout';
import { useAuth } from '../contexts/AuthContext';
import { useGoals } from '../contexts/GoalContext';
import { useOKR } from '../contexts/OKRContext';
import { usePerformance } from '../contexts/PerformanceContext';
import { useNotifications } from '../contexts/NotificationContext';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import { motion } from 'framer-motion';
import { Settings, Maximize2, Minimize2, BarChart3, Target, Rocket, Users } from 'lucide-react';

const ResponsiveGridLayout = WidthProvider(Responsive);

const DashboardWidgets = () => {
  const { user } = useAuth();
  const { analytics: goalAnalytics } = useGoals();
  const { analytics: okrAnalytics } = useOKR();
  const { analytics: performanceAnalytics } = usePerformance();
  const { getUnreadCount, getRecentActivities } = useNotifications();

  const [layouts, setLayouts] = useState({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [expandedWidget, setExpandedWidget] = useState(null);

  // Widget configurations based on user role
  const getWidgetConfigs = () => {
    const baseWidgets = [
      {
        i: 'stats-overview',
        component: StatsOverviewWidget,
        title: 'Performance Overview',
        icon: BarChart3,
        defaultSize: { w: 4, h: 2, x: 0, y: 0 },
        minSize: { w: 3, h: 2 }
      },
      {
        i: 'goals-progress',
        component: GoalsProgressWidget,
        title: 'Goals Progress',
        icon: Target,
        defaultSize: { w: 4, h: 3, x: 4, y: 0 },
        minSize: { w: 3, h: 2 }
      },
      {
        i: 'okr-scorecard',
        component: OKRScorecardWidget,
        title: 'OKR Scorecard',
        icon: Rocket,
        defaultSize: { w: 4, h: 3, x: 8, y: 0 },
        minSize: { w: 3, h: 2 }
      },
      {
        i: 'recent-activity',
        component: RecentActivityWidget,
        title: 'Recent Activity',
        icon: Users,
        defaultSize: { w: 6, h: 4, x: 0, y: 3 },
        minSize: { w: 4, h: 3 }
      },
      {
        i: 'notifications',
        component: NotificationsWidget,
        title: 'Notifications',
        icon: Settings,
        defaultSize: { w: 6, h: 4, x: 6, y: 3 },
        minSize: { w: 4, h: 3 }
      }
    ];

    // Add role-specific widgets
    if (['manager', 'hr', 'executive', 'admin'].includes(user?.role)) {
      baseWidgets.push({
        i: 'team-performance',
        component: TeamPerformanceWidget,
        title: 'Team Performance',
        icon: Users,
        defaultSize: { w: 6, h: 3, x: 0, y: 7 },
        minSize: { w: 4, h: 3 }
      });
    }

    if (['hr', 'executive', 'admin'].includes(user?.role)) {
      baseWidgets.push({
        i: 'company-metrics',
        component: CompanyMetricsWidget,
        title: 'Company Metrics',
        icon: BarChart3,
        defaultSize: { w: 6, h: 3, x: 6, y: 7 },
        minSize: { w: 4, h: 3 }
      });
    }

    return baseWidgets;
  };

  const widgets = getWidgetConfigs();

  // Initialize layouts
  useEffect(() => {
    const savedLayouts = localStorage.getItem(`dashboard-layouts-${user?.id}`);
    if (savedLayouts) {
      setLayouts(JSON.parse(savedLayouts));
    } else {
      // Create default layouts
      const defaultLayouts = {
        lg: widgets.map(widget => ({ ...widget.defaultSize, i: widget.i })),
        md: widgets.map(widget => ({ ...widget.defaultSize, i: widget.i, w: Math.min(widget.defaultSize.w, 6) })),
        sm: widgets.map((widget, index) => ({ w: 12, h: widget.defaultSize.h, x: 0, y: index * widget.defaultSize.h, i: widget.i }))
      };
      setLayouts(defaultLayouts);
    }
  }, [user?.id]);

  const onLayoutChange = (layout, layouts) => {
    setLayouts(layouts);
    if (user?.id) {
      localStorage.setItem(`dashboard-layouts-${user?.id}`, JSON.stringify(layouts));
    }
  };

  const toggleEditMode = () => {
    setIsEditMode(!isEditMode);
  };

  const resetLayout = () => {
    const defaultLayouts = {
      lg: widgets.map(widget => ({ ...widget.defaultSize, i: widget.i })),
      md: widgets.map(widget => ({ ...widget.defaultSize, i: widget.i, w: Math.min(widget.defaultSize.w, 6) })),
      sm: widgets.map((widget, index) => ({ w: 12, h: widget.defaultSize.h, x: 0, y: index * widget.defaultSize.h, i: widget.i }))
    };
    setLayouts(defaultLayouts);
    if (user?.id) {
      localStorage.setItem(`dashboard-layouts-${user?.id}`, JSON.stringify(defaultLayouts));
    }
  };

  const toggleWidgetExpansion = (widgetId) => {
    setExpandedWidget(expandedWidget === widgetId ? null : widgetId);
  };

  return (
    <div className="p-6">
      {/* Edit Controls */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Dashboard</h2>
          <p className="text-gray-600">
            {isEditMode ? 'Drag widgets to customize your layout' : 'Your personalized performance overview'}
          </p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={resetLayout}
            className="px-3 py-2 text-gray-600 hover:text-gray-800 text-sm"
          >
            Reset Layout
          </button>
          <button
            onClick={toggleEditMode}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              isEditMode
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {isEditMode ? 'Save Layout' : 'Customize'}
          </button>
        </div>
      </div>

      {/* Widgets Grid */}
      <ResponsiveGridLayout
        className="layout"
        layouts={layouts}
        onLayoutChange={onLayoutChange}
        breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
        cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
        rowHeight={60}
        isDraggable={isEditMode}
        isResizable={isEditMode}
        margin={[16, 16]}
        containerPadding={[0, 0]}
      >
        {widgets.map((widget) => {
          const WidgetComponent = widget.component;
          const IconComponent = widget.icon;
          const isExpanded = expandedWidget === widget.i;

          return (
            <div key={widget.i}>
              <motion.div
                className="bg-white rounded-lg shadow-sm border border-gray-200 h-full overflow-hidden"
                layout
                transition={{ duration: 0.3 }}
              >
                {/* Widget Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
                  <div className="flex items-center space-x-2">
                    <IconComponent size={18} className="text-gray-600" />
                    <h3 className="font-medium text-gray-900">{widget.title}</h3>
                  </div>
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => toggleWidgetExpansion(widget.i)}
                      className="p-1 text-gray-400 hover:text-gray-600"
                    >
                      {isExpanded ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
                    </button>
                    {isEditMode && (
                      <div className="w-2 h-4 bg-gray-300 rounded cursor-move" />
                    )}
                  </div>
                </div>

                {/* Widget Content */}
                <div className="p-4 h-full overflow-hidden">
                  <WidgetComponent
                    isExpanded={isExpanded}
                    goalAnalytics={goalAnalytics}
                    okrAnalytics={okrAnalytics}
                    performanceAnalytics={performanceAnalytics}
                    userRole={user?.role}
                  />
                </div>
              </motion.div>
            </div>
          );
        })}
      </ResponsiveGridLayout>

      {/* Expanded Widget Modal */}
      {expandedWidget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-screen overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">
                  {widgets.find(w => w.i === expandedWidget)?.title}
                </h2>
                <button
                  onClick={() => toggleWidgetExpansion(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <Minimize2 size={20} />
                </button>
              </div>
              {(() => {
                const widget = widgets.find(w => w.i === expandedWidget);
                const WidgetComponent = widget?.component;
                return WidgetComponent ? (
                  <WidgetComponent
                    isExpanded={true}
                    goalAnalytics={goalAnalytics}
                    okrAnalytics={okrAnalytics}
                    performanceAnalytics={performanceAnalytics}
                    userRole={user?.role}
                  />
                ) : null;
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Individual Widget Components
const StatsOverviewWidget = ({ goalAnalytics, okrAnalytics, performanceAnalytics, isExpanded }) => {
  const stats = [
    {
      label: 'Goals Completed',
      value: goalAnalytics?.completed_goals || 0,
      total: goalAnalytics?.total_goals || 0,
      color: 'text-blue-600',
      bg: 'bg-blue-100'
    },
    {
      label: 'OKR Score',
      value: okrAnalytics?.avg_score ? Math.round(okrAnalytics.avg_score * 100) : 0,
      total: 100,
      color: 'text-indigo-600',
      bg: 'bg-indigo-100',
      suffix: '%'
    },
    {
      label: 'Performance Score',
      value: performanceAnalytics?.avg_overall_score ? Math.round(performanceAnalytics.avg_overall_score * 100) : 0,
      total: 100,
      color: 'text-green-600',
      bg: 'bg-green-100',
      suffix: '%'
    }
  ];

  return (
    <div className="h-full">
      <div className={`grid ${isExpanded ? 'grid-cols-3 gap-6' : 'grid-cols-1 gap-3'} h-full`}>
        {stats.map((stat, index) => (
          <div key={index} className="text-center">
            <div className={`${stat.bg} rounded-lg p-4 mb-2`}>
              <div className={`text-2xl font-bold ${stat.color}`}>
                {stat.value}{stat.suffix || ''}
              </div>
              {!isExpanded && (
                <div className="text-xs text-gray-600">
                  / {stat.total}{stat.suffix || ''}
                </div>
              )}
            </div>
            <div className="text-sm font-medium text-gray-700">{stat.label}</div>
            {isExpanded && (
              <div className="text-xs text-gray-500 mt-1">
                {stat.value} of {stat.total}{stat.suffix || ''}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const GoalsProgressWidget = ({ goalAnalytics, isExpanded }) => {
  const data = {
    labels: ['Completed', 'In Progress', 'Not Started'],
    datasets: [
      {
        data: [
          goalAnalytics?.completed_goals || 0,
          (goalAnalytics?.total_goals || 0) - (goalAnalytics?.completed_goals || 0),
          Math.max(0, 5 - (goalAnalytics?.total_goals || 0))
        ],
        backgroundColor: ['#10B981', '#F59E0B', '#EF4444'],
        borderWidth: 0
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: isExpanded ? 'bottom' : 'right',
        labels: {
          boxWidth: 12,
          font: { size: 11 }
        }
      }
    }
  };

  return (
    <div className="h-full">
      <Doughnut data={data} options={options} />
    </div>
  );
};

const OKRScorecardWidget = ({ okrAnalytics, isExpanded }) => {
  const scorePercentage = okrAnalytics?.avg_score ? okrAnalytics.avg_score * 100 : 0;
  const objectives = okrAnalytics?.total_objectives || 0;

  return (
    <div className="h-full flex flex-col justify-center items-center text-center">
      <div className="mb-4">
        <div className="text-3xl font-bold text-indigo-600">{Math.round(scorePercentage)}%</div>
        <div className="text-sm text-gray-600">Average OKR Score</div>
      </div>
      
      <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
        <div
          className="bg-indigo-600 h-3 rounded-full transition-all duration-300"
          style={{ width: `${scorePercentage}%` }}
        />
      </div>

      <div className="text-sm text-gray-700">
        {objectives} Active Objectives
      </div>

      {isExpanded && (
        <div className="mt-4 grid grid-cols-3 gap-4 w-full">
          <div className="text-center">
            <div className="text-lg font-bold text-green-600">4</div>
            <div className="text-xs text-gray-600">On Track</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-yellow-600">2</div>
            <div className="text-xs text-gray-600">At Risk</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-red-600">1</div>
            <div className="text-xs text-gray-600">Off Track</div>
          </div>
        </div>
      )}
    </div>
  );
};

const RecentActivityWidget = ({ isExpanded }) => {
  const { getRecentActivities } = useNotifications();
  const activities = getRecentActivities(isExpanded ? 10 : 3);

  return (
    <div className="h-full overflow-y-auto">
      <div className="space-y-3">
        {activities.map((activity, index) => (
          <div key={activity.id} className="flex items-start space-x-3">
            <div className="text-sm">{activity.icon}</div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {activity.title}
              </p>
              <p className="text-xs text-gray-600 truncate">
                {activity.description}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const NotificationsWidget = ({ isExpanded }) => {
  const { notifications, getUnreadCount, markAsRead } = useNotifications();
  const unreadCount = getUnreadCount();
  const recentNotifications = notifications.slice(0, isExpanded ? 10 : 3);

  return (
    <div className="h-full">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-medium text-gray-700">
          {unreadCount} unread notifications
        </div>
      </div>
      
      <div className="space-y-2 overflow-y-auto">
        {recentNotifications.map((notification) => (
          <div
            key={notification.id}
            className={`p-2 rounded border ${
              notification.read ? 'bg-gray-50 border-gray-200' : 'bg-blue-50 border-blue-200'
            }`}
            onClick={() => markAsRead(notification.id)}
          >
            <div className="flex items-start space-x-2">
              <span className="text-sm">{notification.icon}</span>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-gray-900 truncate">
                  {notification.title}
                </p>
                <p className="text-xs text-gray-600 truncate">
                  {notification.message}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const TeamPerformanceWidget = ({ isExpanded }) => {
  const data = {
    labels: ['Team A', 'Team B', 'Team C', 'Your Team'],
    datasets: [
      {
        label: 'Performance Score',
        data: [85, 92, 78, 88],
        backgroundColor: 'rgba(59, 130, 246, 0.8)',
        borderColor: 'rgba(59, 130, 246, 1)',
        borderWidth: 1
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: isExpanded },
      title: { display: false }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        display: isExpanded
      },
      x: {
        display: isExpanded
      }
    }
  };

  return (
    <div className="h-full">
      <Bar data={data} options={options} />
    </div>
  );
};

const CompanyMetricsWidget = ({ isExpanded }) => {
  const metrics = [
    { label: 'Employee Satisfaction', value: 87, color: 'bg-green-500' },
    { label: 'Goal Completion Rate', value: 73, color: 'bg-blue-500' },
    { label: 'OKR Alignment', value: 91, color: 'bg-indigo-500' },
    { label: 'Performance Rating', value: 82, color: 'bg-purple-500' }
  ];

  return (
    <div className="h-full">
      <div className="space-y-4">
        {metrics.map((metric, index) => (
          <div key={index}>
            <div className="flex justify-between items-center mb-1">
              <span className="text-sm font-medium text-gray-700">{metric.label}</span>
              <span className="text-sm text-gray-600">{metric.value}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`${metric.color} h-2 rounded-full transition-all duration-300`}
                style={{ width: `${metric.value}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DashboardWidgets;